import type { AIAnalysisRequest, AIAnalysisResult, AIClient } from '$lib/types/ai';
import { AIUtilityService } from './AIUtilityService';
import { AIStreamingService } from './AIStreamingService';

/**
 * AI Correction Analysis Service
 * Handles batch correction analysis and validation
 * Single Responsibility: Batch correction processing and validation
 */
export class AICorrectionAnalysisService {
  private streamingService: AIStreamingService;

  constructor(streamingService: AIStreamingService) {
    this.streamingService = streamingService;
  }

  /**
   * Analyze corrections using AI with optional streaming
   */
  async analyzeCorrections(
    request: AIAnalysisRequest,
    aiClient: AIClient,
    useStreaming: boolean = false
  ): Promise<AIAnalysisResult[]> {
    // Verify client availability
    const isAvailable = aiClient ? await Promise.resolve(aiClient.isAvailable()) : false;
    if (!aiClient || !isAvailable) {
      throw new Error('AI client is not available');
    }

    let results: AIAnalysisResult[];

    // Check if streaming is requested and supported
    if (useStreaming && aiClient.supportsStreaming?.()) {
      results = await this.streamingService.handleStreamingAnalysis(request, aiClient);
    } else {
      // Standard non-streaming analysis
      results = await aiClient.analyzeCorrections(request);
    }
    
    // Validate results
    const validatedResults = this.validateResults(results, request.corrections.length);
    
    return validatedResults;
  }

  /**
   * Validate AI analysis results
   */
  private validateResults(results: AIAnalysisResult[], expectedCount: number): AIAnalysisResult[] {
    return AIUtilityService.validateResults(results, expectedCount);
  }

  /**
   * Prepare analysis request with context optimization
   */
  static prepareAnalysisRequest(
    corrections: any[],
    contextText: string,
    options: {
      maxContextLength?: number;
      includeSurroundingText?: boolean;
      prioritizeRecent?: boolean;
    } = {}
  ): AIAnalysisRequest {
    const {
      maxContextLength = 2000,
      includeSurroundingText = true,
      prioritizeRecent = false
    } = options;

    let optimizedContext = contextText;
    
    // Optimize context length if needed
    if (contextText.length > maxContextLength) {
      if (prioritizeRecent) {
        // Keep the end of the text (most recent changes)
        optimizedContext = '...' + contextText.slice(-(maxContextLength - 3));
      } else {
        // Keep the beginning of the text
        optimizedContext = contextText.slice(0, maxContextLength - 3) + '...';
      }
    }

    return {
      corrections,
      contextText: optimizedContext,
      analysisType: 'corrections',
      options: {
        includeSurroundingText,
        maxResults: corrections.length
      }
    };
  }

  /**
   * Process results and add metadata
   */
  static enrichResults(
    results: AIAnalysisResult[],
    originalRequest: AIAnalysisRequest,
    processingTime: number,
    model: string
  ): AIAnalysisResult[] {
    return results.map((result, index) => ({
      ...result,
      metadata: {
        processingTime,
        model,
        requestIndex: index,
        originalRequestLength: originalRequest.corrections.length,
        contextLength: originalRequest.contextText.length,
        timestamp: Date.now()
      }
    }));
  }

  /**
   * Batch process multiple correction requests
   */
  async batchAnalyzeCorrections(
    requests: AIAnalysisRequest[],
    aiClient: AIClient,
    options: {
      maxConcurrent?: number;
      delayBetweenRequests?: number;
      useStreaming?: boolean;
    } = {}
  ): Promise<AIAnalysisResult[][]> {
    const {
      maxConcurrent = 3,
      delayBetweenRequests = 100,
      useStreaming = false
    } = options;

    const results: AIAnalysisResult[][] = [];
    
    // Process requests in batches to avoid overwhelming the API
    for (let i = 0; i < requests.length; i += maxConcurrent) {
      const batch = requests.slice(i, i + maxConcurrent);
      
      const batchPromises = batch.map(request => 
        this.analyzeCorrections(request, aiClient, useStreaming)
      );
      
      try {
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
        
        // Add delay between batches if specified
        if (delayBetweenRequests > 0 && i + maxConcurrent < requests.length) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenRequests));
        }
      } catch (error) {
        console.error(`Batch ${Math.floor(i / maxConcurrent)} failed:`, error);
        // Add empty results for failed batch
        results.push(...batch.map(() => []));
      }
    }
    
    return results;
  }

  /**
   * Analyze single correction with detailed feedback
   */
  async analyzeSingleCorrection(
    correction: any,
    contextText: string,
    aiClient: AIClient,
    useStreaming: boolean = false
  ): Promise<AIAnalysisResult> {
    const request: AIAnalysisRequest = {
      corrections: [correction],
      contextText,
      analysisType: 'single_correction'
    };

    const results = await this.analyzeCorrections(request, aiClient, useStreaming);
    
    if (results.length === 0) {
      throw new Error('No analysis result returned for single correction');
    }
    
    return results[0];
  }

  /**
   * Compare multiple correction options
   */
  async compareCorrections(
    corrections: any[],
    contextText: string,
    aiClient: AIClient,
    useStreaming: boolean = false
  ): Promise<{
    bestOption: AIAnalysisResult;
    ranking: AIAnalysisResult[];
    comparison: string;
  }> {
    const results = await this.analyzeCorrections(
      { corrections, contextText, analysisType: 'comparison' },
      aiClient,
      useStreaming
    );

    // Sort by confidence and agreement scores
    const ranked = results.sort((a, b) => {
      const scoreA = this.calculateCorrectionScore(a);
      const scoreB = this.calculateCorrectionScore(b);
      return scoreB - scoreA;
    });

    return {
      bestOption: ranked[0],
      ranking: ranked,
      comparison: this.generateComparisonText(ranked)
    };
  }

  /**
   * Calculate correction score for ranking
   */
  private calculateCorrectionScore(result: AIAnalysisResult): number {
    // Simple scoring algorithm - can be enhanced
    let score = 0;
    
    // Base score from reasoning quality (length as proxy)
    score += Math.min(result.reasoning.length / 100, 1) * 30;
    
    // Score for clear decision
    if (result.selectedValue !== 'uncertain') {
      score += 40;
    }
    
    // Score for exception handling
    if (result.isExceptionProcessed) {
      score += 20;
    }
    
    // Bonus for detailed reasoning
    if (result.reasoning.includes('because') || result.reasoning.includes('왜냐하면')) {
      score += 10;
    }
    
    return score;
  }

  /**
   * Generate comparison text for multiple corrections
   */
  private generateComparisonText(rankedResults: AIAnalysisResult[]): string {
    if (rankedResults.length === 0) {
      return 'No corrections to compare.';
    }
    
    if (rankedResults.length === 1) {
      return `Single option analyzed: ${rankedResults[0].reasoning}`;
    }
    
    const best = rankedResults[0];
    const others = rankedResults.slice(1);
    
    let comparison = `Best option: ${best.selectedValue} (${best.reasoning})\n\n`;
    
    if (others.length > 0) {
      comparison += 'Other options considered:\n';
      others.forEach((result, index) => {
        comparison += `${index + 2}. ${result.selectedValue}: ${result.reasoning}\n`;
      });
    }
    
    return comparison;
  }

  /**
   * Get correction analysis statistics
   */
  getAnalysisStatistics(results: AIAnalysisResult[]): {
    totalAnalyzed: number;
    exceptionsProcessed: number;
    uncertainDecisions: number;
    averageReasoningLength: number;
    decisionDistribution: Record<string, number>;
  } {
    const stats = {
      totalAnalyzed: results.length,
      exceptionsProcessed: results.filter(r => r.isExceptionProcessed).length,
      uncertainDecisions: results.filter(r => r.selectedValue === 'uncertain').length,
      averageReasoningLength: results.reduce((sum, r) => sum + r.reasoning.length, 0) / results.length,
      decisionDistribution: {} as Record<string, number>
    };

    // Calculate decision distribution
    results.forEach(result => {
      const decision = result.selectedValue;
      stats.decisionDistribution[decision] = (stats.decisionDistribution[decision] || 0) + 1;
    });

    return stats;
  }
}